import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";
import { CLOUDFRONT_BASE_URL } from "../../../../../config";
import Iconify from "@/components/ui/Iconify";
import { Avatar, Badge } from "@mui/material";
import { green } from "@mui/material/colors";
import { useAuthContext } from "@/context/auth/useAuthContext";
import FavPlaceListCreateFormSliderModal from "./place-fav-list-create-form";
import { enqueueSnackbar } from "notistack";
import { useTranslations } from "@/lib/static-intl";
import {
  addFavoritePlace,
  getPlaceFavListsByUserId,
  removeFavoritePlace,
} from "@/api/places";
import { Bars } from "react-loader-spinner";
import { isApiPlace, getPlaceIdentifier, comparePlaces } from "@/utils/placeUtils";

const PlaceFavSliderModal = ({
  place,
  placeMedia,
  isOpen,
  onClose,
  resetPlaceDetails,
}) => {
  const apiT = useTranslations("api");
  const placesT = useTranslations("places.place_fav_list_slider_modal");
  const { user } = useAuthContext();
  const [isVisible, setIsVisible] = useState(isOpen);
  const [headerMedia, setHeaderMedia] = useState(null);

  const [isInnerModalVisible, setIsInnerModalVisible] = useState(false);

  const [listLoader, setListLoader] = useState(true);
  const [placeFavLists, setPlaceFavLists] = useState([]);

  const [loader, setLoader] = useState(false);
  const [selectedListId, setSelectedListId] = useState([]);

  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [metadataForm, setMetadataForm] = useState({
    note: "",
    nickname: "",
    url: "",
  });

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const resetForm = () => {
    setSelectedListId([]);
    setShowMetadataForm(false);
    setMetadataForm({ note: "", nickname: "", url: "" });
  };

  const handlePlaceListClick = (_id) => {
    if (selectedListId?.includes(_id)) {
      setSelectedListId(selectedListId?.filter((id) => id !== _id));
    } else {
      setSelectedListId([...selectedListId, _id]);
    }
  };

  const handlePlaceFavSubmit = async () => {
    try {
      const extractedPlaceId = getPlaceIdentifier(place);
      // console.log("[DEBUG] handlePlaceFavSubmit - extractedPlaceId:", extractedPlaceId);

      const payload = {
        userId: user?.userId,
        placeId: extractedPlaceId,
        favListIds: selectedListId,
        note:
          metadataForm?.note?.trim()?.length > 0 ? metadataForm?.note : null,
        nickname:
          metadataForm?.nickname?.trim()?.length > 0
            ? metadataForm?.nickname
            : null,
        url: metadataForm?.url?.trim()?.length > 0 ? metadataForm?.url : null,
      };
      setLoader(true);
      const listsThatAreRemoved = placeFavLists?.filter(
        (list) => !selectedListId?.includes(list?.id)
      );

      // console.log("[DEBUG] listsThatAreRemoved:", listsThatAreRemoved);

      const favToBeRemoved = listsThatAreRemoved
        ?.flatMap((list) => list?.listItems)
        ?.filter((item) => {
          // console.log("[DEBUG] Checking item:", item, "against place:", place);
          
          // Use the utility function for consistent comparison
          const match = comparePlaces(place, item);
          console.log("[DEBUG] Place comparison result:", match);
          return match;
        })
        ?.map((item) => item?.id);

      // console.log("[DEBUG] favToBeRemoved:", favToBeRemoved);
      if (Array.isArray(favToBeRemoved) && favToBeRemoved?.length > 0)
        await removeFavoritePlace(favToBeRemoved);

      if (Array.isArray(selectedListId) && selectedListId?.length > 0) {
        await addFavoritePlace(payload);
      } else if (!(Array.isArray(favToBeRemoved) && favToBeRemoved?.length > 0)) {
        // 고른 목록이 없으면 저장할 것도 지울 것도 없다. 조용히 닫히면
        // 사용자는 저장된 줄 안다(2026-07-31 실제로 그렇게 잃었다).
        enqueueSnackbar("저장할 목록을 선택해 주세요", { variant: "warning" });
        setLoader(false);
        return;
      }

      resetForm();
      setIsVisible(false);
      setTimeout(() => {
        onClose();
        resetPlaceDetails();
      }, 200);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setLoader(false);
    }
  };

  const fetchPlaceFavLists = async () => {
    try {
      if (!user?.userId) return;
      setListLoader(true);
      const response = await getPlaceFavListsByUserId({
        userId: user?.userId,
        placeId: getPlaceIdentifier(place),
      });
      if (response && Array.isArray(response)) {
        const lists = response?.filter((list) => list?.listItems?.length > 0);
        setSelectedListId(lists?.map((list) => list?.id));
        setPlaceFavLists(response);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setListLoader(false);
    }
  };

  useEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const allMedia = (placeMedia || [])?.filter(
        (media) => media.mediaPath !== null
      );
      if (allMedia && Array.isArray(allMedia) && allMedia?.length > 0) {
        setHeaderMedia(allMedia[0]);
      }
    }
  }, [placeMedia, isOpen]);

  useEffect(() => {
    if (user?.userId && isOpen) {
      resetForm();
      fetchPlaceFavLists();
    }
  }, [user, isOpen]);

  return (
    <AnimatePresence>
      {isVisible && (
        <div
          className="absolute z-[90] bg-black/60 top-0 left-0 right-0 bottom-0"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute top-12 bottom-0 left-0 right-0 bg-white dark:bg-dark_bg_grey rounded-t-2xl overflow-hidden z-[91]"
            onClick={(e) => e.stopPropagation()}
          >
            {listLoader ? (
              <div className="w-full h-full flex items-center justify-center">
                <Bars
                  height="60"
                  width="60"
                  color="#4fa94d"
                  ariaLabel="bars-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                />
              </div>
            ) : (
              <div className="w-full h-full flex flex-col">
                <div className="w-full flex py-3 px-3">
                  <div className="w-full flex items-center justify-center gap-2">
                    {headerMedia && headerMedia?.mediaPath && (
                      <div className="w-8 h-8 rounded-full">
                        <Image
                          src={`${CLOUDFRONT_BASE_URL}/${headerMedia?.mediaPath}`}
                          alt="headerImg"
                          width={50}
                          height={50}
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                    )}
                    <span className="w-fit text-xl font-semibold tracking-tight">
                      {place?.name}
                    </span>
                  </div>
                  <button type="button" onClick={handleClose} className="w-fit">
                    <Iconify
                      icon={"pepicons-pencil:angle-down"}
                      className={"!min-w-6 !w-6 !h-6"}
                    />
                  </button>
                </div>
                <div className="w-full h-full max-h-full overflow-y-auto thin-scrollbar flex flex-col gap-5 py-3 px-4">
                  {showMetadataForm ? (
                    <div className="w-full flex flex-col gap-2">
                      <div className="w-full flex items-center justify-between border-b dark:border-brown py-2 px-2">
                        <input
                          type="text"
                          placeholder={placesT("leave_a_note_pl")}
                          className="w-full border-none outline-none text-sm caret-red"
                          value={metadataForm.note}
                          onChange={(e) =>
                            setMetadataForm({
                              ...metadataForm,
                              note: e.target.value,
                            })
                          }
                          maxLength={50}
                        />
                        <span className="text-xs text-nowrap">
                          {metadataForm?.note?.length}
                          <span className="text-black/40 dark:text-white/80">
                            /50
                          </span>
                        </span>
                      </div>
                      <div className="w-full flex items-center justify-between gap-2 py-2 px-2 bg-gray-100 dark:bg-brown rounded">
                        <input
                          type="text"
                          placeholder={placesT("nickname_pl")}
                          className="w-full border-none outline-none bg-transparent text-sm caret-red"
                          value={metadataForm.nickname}
                          onChange={(e) =>
                            setMetadataForm({
                              ...metadataForm,
                              nickname: e.target.value,
                            })
                          }
                          maxLength={25}
                        />
                        <span className="text-xs text-nowrap">
                          {metadataForm?.nickname?.length}
                          <span className="text-black/40 dark:text-white/80">
                            /25
                          </span>
                        </span>
                      </div>
                      <div className="w-full flex items-center justify-between gap-2 py-2 px-2 bg-gray-100 dark:bg-brown rounded">
                        <input
                          type="url"
                          placeholder={placesT("relevant_url_pl")}
                          className="w-full border-none outline-none bg-transparent text-sm caret-red"
                          value={metadataForm.url}
                          onChange={(e) =>
                            setMetadataForm({
                              ...metadataForm,
                              url: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowMetadataForm(true)}
                      className="w-full py-2 px-2 bg-surface-2 dark:bg-surface-2dark text-ink dark:text-ink_on_dark rounded-md text-center text-nowrap font-medium"
                    >
                      {placesT("add_notes_nicknames_url")}
                    </button>
                  )}

                  <div className="w-full flex flex-col border-b dark:border-brown divide-y dark:divide-brown">
                    <button
                      type="button"
                      onClick={() => setIsInnerModalVisible(true)}
                      className="w-full flex items-center gap-2 py-3 cursor-pointer"
                    >
                      <Iconify
                        icon={"formkit:add"}
                        className={"!min-w-6 !w-6 !h-6 !text-opacity-60"}
                      />
                      <span className="text-nowrap opacity-60">
                        {placesT("create_a_new_list")}
                      </span>
                    </button>
                    <div className="w-full flex flex-col divide-y dark:divide-brown">
                      {placeFavLists?.map((placeFavList, index) => (
                        <div
                          key={index}
                          className="w-full flex items-center justify-between gap-2 py-3 cursor-pointer"
                          onClick={() => handlePlaceListClick(placeFavList?.id)}
                        >
                          <div className="w-fit flex items-center gap-1">
                            {placeFavList?.isPublic ? (
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: placeFavList?.color || green[500],
                                }}
                              >
                                <Iconify
                                  icon={"material-symbols:star"}
                                  className={"!min-w-4 !w-4 !h-4 !text-white"}
                                />
                              </Avatar>
                            ) : (
                              <Badge
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                badgeContent={
                                  <div className="bg-white rounded-full w-3 h-3 flex items-center justify-center">
                                    <Iconify
                                      icon={"material-symbols:lock"}
                                      className={"!min-w-4 !w-2.5 !h-2.5 !text-black"}
                                    />
                                  </div>
                                }
                              >
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: placeFavList?.color || green[500],
                                  }}
                                >
                                  <Iconify
                                    icon={"material-symbols:star"}
                                    className={"!min-w-4 !w-4 !h-4 !text-white"}
                                  />
                                </Avatar>
                              </Badge>
                            )}
                            <span className="text-nowrap font-medium ml-2">
                              {placeFavList?.listName}
                            </span>
                            <span className="text-black/40">
                              {placeFavList?._count?.listItems || 0}
                            </span>
                            {placeFavList?.type === "NEW" && (
                              <span className="text-red font-semibold ml-2">
                                {placesT("new")}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="flex items-center"
                            onClick={() =>
                              handlePlaceListClick(placeFavList?.id)
                            }
                          >
                            {selectedListId?.includes(placeFavList?.id) ? (
                              <Iconify
                                icon={"icon-park-solid:check-one"}
                                className={
                                  "!text-action !min-w-6 !w-6 !h-6 scale-95"
                                }
                              />
                            ) : (
                              <Iconify
                                icon={"ei:check"}
                                className={
                                  "!min-w-7 !w-7 !h-7 !text-opacity-60"
                                }
                              />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="w-full flex items-center justify-center py-4 px-4 border-t dark:border-brown">
                  <button
                    type="button"
                    disabled={!selectedListId?.length || loader}
                    onClick={handlePlaceFavSubmit}
                    className={`w-full p-2 rounded-md ${
                      selectedListId?.length && !loader
                        ? "bg-red hover:bg-red/80"
                        : "bg-red/50 pointer-events-none cursor-not-allowed"
                    } text-white text-lg text-nowrap transition flex items-center justify-center gap-1`}
                  >
                    {loader && (
                      <Iconify
                        icon="line-md:loading-twotone-loop"
                        className={"!text-white animate-spin"}
                      />
                    )}
                    {placesT("save")}
                  </button>
                </div>
              </div>
            )}
            <FavPlaceListCreateFormSliderModal
              isOpen={isInnerModalVisible}
              onClose={() => setIsInnerModalVisible(false)}
              setPlaceFavLists={setPlaceFavLists}
              onListCreated={(createdList) => {
                if (!createdList?.id) return;
                setSelectedListId((prev) =>
                  prev?.includes(createdList.id) ? prev : [...(prev || []), createdList.id]
                );
              }}
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PlaceFavSliderModal;
